import React, { useMemo, useRef } from 'react'

import { DurationInput } from '@components/duration-input.component'
import { ActionIcon } from '@components/mantine/action-icon.component'
import { NumberInput } from '@components/mantine/number-input.component'
import { TextInput } from '@components/mantine/text-input.component'
import { RichText } from '@components/rich-text/rich-text.component'
import { Select } from '@components/mantine/select.component'
import { $generateNodesFromDOM } from '@lexical/html'
import { FieldHeaderContent } from '@src/pages/workflow/client/fields/field-header-content.component'
import { AssistedResponseModal } from '@src/pages/workflow/client/workflow-refactor/assisted-response-modal.component'
import {
  TEXT_TYPE_CONSTANTS,
  lengthOptions,
} from '@src/pages/workflow/fields.helper'
import { modalService } from '@utils/modal.service'
import { $getRoot } from 'lexical'
import { Control, useController } from 'react-hook-form'

type WorkflowTextInputProps = {
  field: Field
  readOnly?: boolean
  isRequired?: boolean
  section: ActivitySection
  control: Control
}

function getRules(field: Field, isRequired?: boolean) {
  const rules: Record<string, any> = {}
  if (field.textType === TEXT_TYPE_CONSTANTS.LONG) {
    rules.validate = (value: string) => {
      if (!value)
        return isRequired ? 'This field is required' : false
    }
  } else {
    rules.required =
      isRequired ? 'This field is required' : false
  }
  return rules
}

export function WorkflowTextInput({
  field,
  readOnly,
  isRequired,
  section,
  control,
}: WorkflowTextInputProps) {
  const editorRef = useRef<any>(null)

  const {
    field: fieldProps,
    fieldState: { error },
  } = useController({
    disabled: readOnly,
    name: `${field.version}_${field.id}`,
    control,
    rules: getRules(field, isRequired),
  })

  const distanceUnit = useMemo(() => {
    if (!fieldProps.value?.unit) return lengthOptions.find((o: any) => o.id === 'mi')
    return lengthOptions.find((option: any) => option.id === fieldProps.value?.unit)
  }, [fieldProps.value?.unit])

  const lengthSelectOptions = useMemo(
    () => lengthOptions.map((opt: any) => ({ value: opt.id, label: opt.name })),
    [],
  )

  return (
    <div>
      <FieldHeaderContent
        field={field}
        isRequired={isRequired}
        section={section}
        actions={
          <>
            <ActionIcon
              size="sm"
              onClick={() => {
                modalService
                  .render(AssistedResponseModal, { field, section })
                  .then(({ response }: { response: string }) => {
                    if (field.textType === TEXT_TYPE_CONSTANTS.LONG) {
                      const editor = editorRef.current
                      editor.update(() => {
                        const parser = new DOMParser()
                        const dom = parser.parseFromString(
                          `<p>${response}</p>`,
                          'text/html',
                        )
                        const nodes = $generateNodesFromDOM(editor, dom)

                        const root = $getRoot()
                        root.clear()
                        root.append(...nodes)
                      })
                    } else if (
                      field.textType === TEXT_TYPE_CONSTANTS.DISTANCE
                    ) {
                      console.log(response)
                      fieldProps.onChange(response)
                    } else {
                      fieldProps.onChange(response)
                    }
                  })
              }}
              icon="wand-magic-sparkles"
            />
          </>
        }
      />
      {field.textType === TEXT_TYPE_CONSTANTS.SHORT && (
        <TextInput
          {...fieldProps}
          autoComplete="off"
          disabled={readOnly}
          error={error?.message}
        />
      )}
      {field.textType === TEXT_TYPE_CONSTANTS.LONG && (
        <>
          <RichText
            {...fieldProps}
            id={`${field.id}-rich-text`}
            initialValue={fieldProps?.value}
            disabled={fieldProps.disabled}
            editorRef={editorRef}
            error={error?.message}
          />
        </>
      )}
      {field.textType === TEXT_TYPE_CONSTANTS.NUMBER && (
        <div className="flex items-center gap-2 [&_.form-input-container]:w-auto">
          <NumberInput
            {...fieldProps}
            value={Number(fieldProps?.value)}
            min={0}
            error={error?.message}
          />
          <span>{field.dataLabel}</span>
        </div>
      )}
      {field.textType === TEXT_TYPE_CONSTANTS.DISTANCE && (
        <div className="flex items-center gap-2 [&_.form-input-container]:w-auto">
          <NumberInput
            {...fieldProps}
            onChange={(text: string) => {
              const newDistance = {
                unit: distanceUnit.id,
                value: text,
              }
              fieldProps.onChange(newDistance)
            }}
            value={Number(fieldProps?.value?.value) || 0}
            min={0}
            placeholder="Distance"
            error={error?.message}
          />
          <div style={{ width: 150 }}>
            <Select
              className="ml-2"
              disabled={readOnly}
              onChange={(value: string | null) => {
                const newDistance = {
                  unit: value,
                  value: fieldProps?.value?.value,
                }
                fieldProps.onChange(newDistance)
              }}
              value={distanceUnit?.id}
              data={lengthSelectOptions}
            />
          </div>
        </div>
      )}
      {field.textType === TEXT_TYPE_CONSTANTS.PERCENTAGE && (
        <div className="flex items-center [&_.form-input-container]:w-auto">
          <NumberInput
            {...fieldProps}
            value={Number(fieldProps?.value)}
            error={error?.message}
            min={0}
            max={100}
            suffix="%"
          />
        </div>
      )}
      {field.textType === TEXT_TYPE_CONSTANTS.DURATION && (
        <div className="flex items-center [&_.form-input-container]:w-auto p-3 bg-gray-50 rounded">
          <DurationInput
            {...({ onChange: fieldProps.onChange, value: fieldProps?.value || 0, error: error?.message } as any)}
          />
        </div>
      )}
    </div>
  )
}
